import { env } from './env';
import { ApiError } from './api-error';

type Json = unknown;

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: Json;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Skip the 401->refresh->retry dance. Used by /auth/refresh itself. */
  skipAuthRefresh?: boolean;
  signal?: AbortSignal;
}

/** Runtime hooks injected by the AuthContext and ProjectContext providers. */
export interface ApiClientHooks {
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  getProjectId: () => string | null;
  /** Called when refresh fails, so the app can redirect to /login. */
  onAuthLost: () => void;
}

let hooks: ApiClientHooks = {
  getAccessToken: () => null,
  setAccessToken: () => undefined,
  getProjectId: () => null,
  onAuthLost: () => undefined,
};

export function configureApiClient(next: ApiClientHooks): void {
  hooks = next;
}

function buildUrl(path: string, query?: ApiRequestOptions['query']): string {
  const base = env.apiBaseUrl;
  const isAbsolute = /^https?:\/\//i.test(base);
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const full = `${base}${suffix}`;

  // No query params? Return the string as-is so absolute URLs keep their origin.
  if (!query) return full;

  const hasAny = Object.values(query).some((v) => v !== undefined && v !== null);
  if (!hasAny) return full;

  const tmp = new URL(full, window.location.origin);
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    tmp.searchParams.set(k, String(v));
  }
  // For absolute (prod) URLs, return the fully-qualified form so fetch hits
  // the real API origin. For relative paths ('/api/...' in dev), return the
  // path-only form so Vite's proxy can intercept and forward.
  return isAbsolute ? tmp.toString() : tmp.pathname + tmp.search;
}

async function rawFetch(path: string, init: RequestInit, query?: ApiRequestOptions['query']) {
  const headers = new Headers(init.headers);
  const token = hooks.getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const projectId = hooks.getProjectId();
  if (projectId) headers.set('X-Project-Id', projectId);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');

  return fetch(buildUrl(path, query), {
    ...init,
    headers,
    credentials: 'include',
  });
}

// Ensure only one refresh-in-flight at a time so parallel 401s share the retry.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { access_token?: string };
      if (!data.access_token) return false;
      hooks.setAccessToken(data.access_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, query, skipAuthRefresh, signal } = options;
  const init: RequestInit = { method };
  if (signal) init.signal = signal;
  if (body !== undefined) init.body = JSON.stringify(body);

  let res = await rawFetch(path, init, query);

  if (res.status === 401 && !skipAuthRefresh && hooks.getAccessToken() !== null) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await rawFetch(path, init, query);
    } else {
      hooks.setAccessToken(null);
      hooks.onAuthLost();
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }

  if (!res.ok) {
    throw await ApiError.fromResponse(res);
  }

  const text = await res.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

export const api = {
  get: <T = unknown>(path: string, query?: ApiRequestOptions['query']) =>
    apiRequest<T>(path, { method: 'GET', ...(query !== undefined ? { query } : {}) }),
  post: <T = unknown>(path: string, body?: Json) =>
    apiRequest<T>(path, { method: 'POST', ...(body !== undefined ? { body } : {}) }),
  patch: <T = unknown>(path: string, body?: Json) =>
    apiRequest<T>(path, { method: 'PATCH', ...(body !== undefined ? { body } : {}) }),
  put: <T = unknown>(path: string, body?: Json) =>
    apiRequest<T>(path, { method: 'PUT', ...(body !== undefined ? { body } : {}) }),
  delete: <T = unknown>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};
